import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { authAPI, platformAPI } from '../api';
import PlatformFooter from '../components/PlatformFooter';

const InviteAccept = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ptx, setPtx] = useState(null);

  useEffect(() => {
    const checkInvite = async () => {
      try {
        const { data } = await authAPI.checkInvite(token);
        setInvite(data);
        setUsername(data.username || '');
      } catch (err) {
        if (err.response?.status === 410) {
          setError('This invite has expired. Please contact your admin for a new one.');
        } else {
          setError('Invalid invite link. Please check the link or contact your admin.');
        }
      } finally {
        setLoading(false);
      }
    };
    checkInvite();
    platformAPI.getTexts().then(res => setPtx(res.data)).catch(() => {});
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await authAPI.acceptInvite(token, username, password);

      Cookies.set('kahla_token', data.access_token, {
        expires: 1,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
      Cookies.set('kahla_role', data.role || 'user', {
        expires: 1,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '40px auto' }}></div>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="login-page">
        <div className="login-container" style={{ textAlign: 'center' }}>
          <div className="login-logo">MAESTRA</div>
          <h1 className="login-title" style={{ fontSize: '20px' }}>Invite Error</h1>
          <p style={{ color: 'var(--kahla-text-light)', fontSize: '14px', marginBottom: '24px' }}>{error}</p>
          <button className="btn-secondary" onClick={() => navigate('/login')} style={{ fontSize: '12px', padding: '10px 20px' }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div className="login-container">
        <div className="login-logo">MAESTRA</div>
        <h1 className="login-title" data-testid="invite-page-title">{ptx?.invite_title || 'Set Up Your Account'}</h1>

        <p style={{
          textAlign: 'center',
          fontSize: '14px',
          color: 'var(--kahla-text-light)',
          marginBottom: '32px'
        }}>
          <span data-testid="invite-page-description">{ptx?.invite_description || 'Welcome! Complete your account to get started.'}</span>
          {invite?.email && (
            <span style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
              Invited as: <strong>{invite.email}</strong>
            </span>
          )}
        </p>

        {error && <div className="login-error">{error}</div>}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label>Username</label>
            <input
              data-testid="invite-accept-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Choose your username"
              required
            />
          </div>

          <div className="login-field">
            <label>Password</label>
            <input
              data-testid="invite-accept-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password (min 6 chars)"
              required
            />
          </div>

          <div className="login-field">
            <label>Confirm Password</label>
            <input
              data-testid="invite-accept-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            data-testid="invite-accept-submit"
            disabled={submitting}
            style={{ width: '100%', marginTop: '12px' }}
          >
            {submitting ? 'Setting up...' : 'Create Account'}
          </button>
        </form>
      </div>
      </div>

      <footer data-testid="invite-footer" style={{
        width: '100vw',
        padding: '24px',
        textAlign: 'center',
        borderTop: '1px solid var(--kahla-border)',
        marginTop: 'auto',
      }}>
        <PlatformFooter />
      </footer>
    </div>
  );
};

export default InviteAccept;
